import request from '@teetsh/app/src/utils/request';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import useNotification from 'containers/App/Notifications/useNotification';
import { useTranslation } from 'react-i18next';
import { PdfResponse } from 'types/pdf';

interface Props {
  schoolyearId: string;
  schoolId: string;
  onStart: () => void;
  onFinished: () => void;
  onError: () => void;
  classrooms: string[];
  selectedDays?: number[];
  title?: string;
  splitMorningAfternoon?: boolean;
  edtName: string;
  edtId: string;
}

function fetchEDTAPI(s3Url: string, edtName: string) {
  return fetch(s3Url)
    .then(response => response.blob())
    .then(response => {
      saveAs(response, `${edtName}.pdf`);
    });
}

async function downloadEDT(props: Props) {
  const {
    schoolyearId,
    schoolId,
    onStart,
    onFinished,
    onError,
    classrooms,
    selectedDays,
    title,
    splitMorningAfternoon,
    edtId,
    edtName,
  } = props;

  onStart();

  let requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/emplois-du-temps/${edtId}/export/pdf`;

  // Add query parameters if provided
  const params = new URLSearchParams();
  if (selectedDays && selectedDays.length > 0) {
    params.set('selectedDays', selectedDays.join(','));
  }
  if (title && title.trim().length > 0) {
    params.set('title', title);
  }
  if (splitMorningAfternoon) {
    params.set('splitMorningAfternoon', 'true');
  }
  if (params.toString()) {
    requestURL = `${requestURL}?${params.toString()}`;
  }

  try {
    const response: PdfResponse = await request(requestURL, {
      method: 'POST',
      body: classrooms,
    });
    const token = response.pdf_token;
    await fetchEDTAPI(token, edtName);
  } catch {
    onError();
  } finally {
    onFinished();
  }
}

export function useDownloadEDT() {
  const { schoolyear, school } = useSchoolSchoolyear();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { notifyError } = useNotification();
  const { t } = useTranslation(['common']);

  const download = ({
    classrooms,
    selectedDays,
    title,
    splitMorningAfternoon,
    edtId,
    edtName,
    callback,
  }: {
    classrooms: string[];
    selectedDays?: number[];
    title?: string;
    splitMorningAfternoon?: boolean;
    edtId: string;
    edtName: string;
    callback?: () => void;
  }) =>
    downloadEDT({
      classrooms,
      selectedDays,
      title,
      splitMorningAfternoon,
      edtId,
      edtName,
      schoolyearId: schoolyear.id,
      schoolId: school.id,
      onStart: () => setIsDownloading(true),
      onFinished: () => {
        setIsDownloading(false);
        if (callback) {
          callback();
        }
      },
      onError: () => notifyError({ title: t('common:messages.genericError') }), // todo specific error message
    });

  return {
    download,
    isDownloading,
  };
}
